import { useCallback, useEffect, useState } from "react";
import { Table, Badge, Button, UncontrolledTooltip, Row, Col, Input } from "reactstrap";
import { withNamespaces } from "react-i18next";
import DateUtils from 'src/services/utils/DateUtils';
import  { INTERNATIONAL_DATE_FORMAT } from 'src/common/constants';
import ManagementTeamImage from "src/modules/3rd-party-management/assets/images/management-team.svg";
import Accordion from 'react-bootstrap/Accordion';
import { RiCheckLine, RiCloseLine, RiInformationLine } from "react-icons/ri";
import ScreeningConfirmationModal from "../../../../components/screening-confirmation-modal";
import MonitoringConfirmationModal from "../../../../components/monitoring-confirmation-modal";
import StopMonitoringConfirmationModal from "../../../../components/stop-monitoring-confirmation-modal";
import { isFeatureEnabledForActiveModule } from "src/helpers/module_helper";

const dateUtils = new DateUtils();

function convertApiDate(dateStr) {
    return dateUtils.convertDateToDate(dateStr, INTERNATIONAL_DATE_FORMAT)
}

const ManagementTeam = ({
    t,
    activeAccordion, 
    setActiveAccordion,
    infoQuery,
    supplierData
}) => {
    const [ selectedPersonToScreen, setSelectedPersonToScreen ] = useState();
    const [ selectedPersonToMonitor, setSelectedPersonToMonitor ] = useState(null);
    const [ selectedRowsForBulkAction, setSelectedRowsForBulkAction ] = useState([]);
    const [ selectedPersonToStopMonitoring, setSelectedPersonToStopMonitoring ] = useState(null);
    const [ showBulkSreenConfirmationModal, setShowBulkScreenConfirmationModal ] = useState(false);

    const [ team, setTeam ] = useState([]);

    useEffect(() => {
        if (infoQuery.data?.management_team) {
            setTeam(infoQuery.data?.management_team)
        }
    }, [ infoQuery.data ]);

    const handleScreenCompleted = useCallback(() => {
        setSelectedPersonToScreen(null);
    }, [ ]);

    const handleMonitorCompleted = useCallback(() => {
        setTeam((prev) =>
            prev.map((person) => {
                if (person.id === selectedPersonToMonitor?.id) {
                    return {
                        ...person,
                        has_monitoring_follow_list_item: true
                    };
                }

                return person;
            })
        );

        setSelectedPersonToMonitor(null);
    }, [ selectedPersonToMonitor ]);

    const handleStopMonitoringCompleted = useCallback(() => {
        setTeam((prev) =>
            prev.map((person) => {
                if (person.id === selectedPersonToStopMonitoring?.id) {
                    return {
                        ...person,
                        has_monitoring_follow_list_item: false
                    };
                }

                return person;
            })
        );

        setSelectedPersonToStopMonitoring(null);
    }, [ selectedPersonToStopMonitoring ]);

    const onBulkCheckboxSelectAll = useCallback((e) => {
        if (e.target.checked) {
            setSelectedRowsForBulkAction(team.map((item) => item.id));
        } else {
            setSelectedRowsForBulkAction([]);
        }
    }, [ team ]);

    const handlePersonCheckboxCheck = useCallback((e) => {
        const { value, checked } = e.target;
        const id = Number(value);

        setSelectedRowsForBulkAction((prev) => {
            if (checked) {
                return [...prev, id];
            } else {
                return prev.filter((item) => item !== id);
            }
        });
    }, []);

    if (infoQuery.isFetching) {
        return (
            <Row>
                <Col 
                    sm="12" 
                    className="mt-4 mb-4"
                >
                    <div 
                        className="dt-field dt-skeleton" 
                        style={{ width: '100%', height: '100px' }}
                    />
                </Col>
            </Row>
        )
    }

    const BooleanIcon = ({ value }) => (
        <div className="d-flex align-items-center gap-1">
            {value ? (
                <RiCheckLine size={16} className="text-success" />
            ) : (
                <RiCloseLine size={16} className="text-danger" />
            )}
        </div>
    );

    const statusColor = (status) => {
        switch (status) {
            case "CLEAN":
                return "bg-success";
            case "SANCTIONED":
                return "bg-danger";
            case "UNKNOWN":
                return "bg-secondary";
            default:
                return "bg-warning";
        }
    };

    return (
        <Accordion.Item
            onClick={() => setActiveAccordion('management-team')}
            className={activeAccordion === 'management-team' ? 'active' : ''}
            eventKey={'management-team'}
        >
            <Accordion.Header>
                <img
                    src={ManagementTeamImage}
                    alt=""
                />
                <span>
                    {t('Management Team')}
                </span>
            </Accordion.Header>

            <Accordion.Body>
                <div className="shareholders-section mb-5" role="region" aria-labelledby="shareholders-heading">

                    <div
                        className="d-flex justify-content-end mb-3"
                    >
                        <span id="bulk-action-start-compliance-screening-btn">
                            <Button
                                color="primary"
                                outline
                                disabled={!selectedRowsForBulkAction.length}
                                onClick={() => setShowBulkScreenConfirmationModal(true)}
                            >
                                {!selectedRowsForBulkAction.length ? (
                                    <UncontrolledTooltip
                                        target="bulk-action-start-compliance-screening-btn"
                                    >
                                        {t('Select at least one team member to start screening.')}
                                    </UncontrolledTooltip>
                                ) : null}

                                {t('Add to Screening')}
                            </Button>
                        </span>
                    </div>

                    <div style={{ maxHeight: "320px", overflowY: "auto" }}>
                        <Table hover>
                            <caption className="visually-hidden">
                                {t('List of management team related to this organization.')}
                            </caption>
                            <thead
                                style={{
                                    position: 'sticky',
                                    top: 0,
                                    background: 'white',
                                    zIndex: 2
                                }}
                            >
                                <tr>
                                    <th
                                        style={{
                                            width: '25px'
                                        }}
                                    >
                                        <Input
                                            type="checkbox"
                                            onChange={onBulkCheckboxSelectAll}
                                        />
                                    </th>
                                    <th>{t('Full Name')}</th>
                                    <th>{t('Position / Role')}</th>
                                    <th>
                                        <span className="me-1">
                                            {t('PEP Status')}
                                        </span>
                                        <RiInformationLine
                                            id="pepTip"
                                            size={14}
                                        />
                                        <UncontrolledTooltip
                                            target="pepTip"
                                        >
                                            {t('Politically Exposed Person status')}
                                        </UncontrolledTooltip>
                                    </th>
                                    <th>
                                        <span className="me-1">
                                            {t('Sanction Status')}
                                        </span>
                                        <RiInformationLine
                                            id="sanctionTip"
                                            size={14}
                                        />
                                        <UncontrolledTooltip
                                            target="sanctionTip"
                                        >
                                            {t('Status on sanctions lists')}
                                        </UncontrolledTooltip>
                                    </th>
                                    <th>
                                        {t('Sanction Hits')}
                                    </th>
                                    <th>
                                        {t('Date of Birth')}
                                    </th>
                                    <th>
                                        {t('Title')}
                                    </th>
                                    <th>
                                        <span className="me-1">
                                            {t('Active')}
                                        </span>
                                        <RiInformationLine
                                            id="activeTip"
                                            size={14}
                                        />
                                        <UncontrolledTooltip
                                            target="activeTip"
                                        >
                                            {t('Indicates if the person is currently active')}
                                        </UncontrolledTooltip>
                                    </th>
                                    <th>
                                        {t('Appointment Date')}
                                    </th>
                                    <th>{t('Monitoring')}</th>
                                    <th>
                                        {t('Action')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {team.length > 0 ? (
                                    team.map((person) => (
                                        <tr key={person.id}>
                                            <td>
                                                <Input
                                                    type="checkbox"
                                                    checked={selectedRowsForBulkAction.includes(person.id)}
                                                    onChange={handlePersonCheckboxCheck}
                                                    value={person.id}
                                                />
                                            </td>

                                            <td>{person.fullName || "-"}</td>
                                            <td>{person.positions || "-"}</td>
                                            <td>
                                                <Badge
                                                    className={`bg-${statusColor(person.pepsStatus)}`}
                                                >
                                                    {person.pepsStatus}
                                                </Badge>
                                            </td>
                                            <td>
                                                <Badge
                                                    className={`bg-${statusColor(person.sanctionStatus)}`}
                                                >
                                                    {person.sanctionStatus}
                                                </Badge>
                                            </td>
                                            <td>{person.sanctionHits}</td>
                                            <td>{convertApiDate(person.dateOfBirth)}</td>
                                            <td>{person.title || "-"}</td>
                                            <td>
                                                <BooleanIcon
                                                    value={person.isActive}
                                                />
                                            </td>
                                            <td>{convertApiDate(person.appointmentDate)}</td>
                                            <td>
                                                <span className="d-flex">
                                                    <BooleanIcon
                                                        value={person.has_monitoring_follow_list_item}
                                                    />
                                                    {t(person.has_monitoring_follow_list_item ? 'Yes' : 'No')}
                                                </span>
                                            </td>
                                            <td
                                                style={{
                                                    width: '300px'
                                                }}
                                            >
                                                <div className="d-flex gap-2">
                                                    <Button
                                                        color="primary"
                                                        onClick={() => setSelectedPersonToScreen(person)}
                                                    >
                                                        {t('Screen')}
                                                    </Button>

                                                    {isFeatureEnabledForActiveModule('compliance_monitoring_activation') && (
                                                        <>
                                                            {person.has_monitoring_follow_list_item ? (
                                                                <Button
                                                                    color="warning"
                                                                    onClick={() => setSelectedPersonToStopMonitoring(person)}
                                                                >
                                                                    {t('Stop Monitoring')}
                                                                </Button>
                                                            ) : (
                                                                <Button
                                                                    color="success"
                                                                    onClick={() => setSelectedPersonToMonitor(person)}
                                                                >
                                                                    {t('Add to Monitoring')}
                                                                </Button>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="14">
                                            <div className="text-center">
                                                <p>{t('No management team members found.')}</p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                </div>

                <ScreeningConfirmationModal
                    isOpen={!!selectedPersonToScreen || showBulkSreenConfirmationModal}
                    toggle={() => {
                        setSelectedPersonToScreen(null);
                        setShowBulkScreenConfirmationModal(false);
                    }}
                    isBulkAction={showBulkSreenConfirmationModal}
                    supplierId={supplierData?.id}
                    people={showBulkSreenConfirmationModal ? selectedRowsForBulkAction : (selectedPersonToScreen ? [selectedPersonToScreen] : [])}
                    onCompleted={handleScreenCompleted}
                />

                <MonitoringConfirmationModal
                    isOpen={!!selectedPersonToMonitor}
                    toggle={() => setSelectedPersonToMonitor(null)}
                    person={selectedPersonToMonitor}
                    supplierId={supplierData?.id}
                    onCompleted={handleMonitorCompleted}
                />

                <StopMonitoringConfirmationModal
                    isOpen={!!selectedPersonToStopMonitoring}
                    toggle={() => setSelectedPersonToStopMonitoring(null)}
                    person={selectedPersonToStopMonitoring}
                    onCompleted={handleStopMonitoringCompleted}
                />
            </Accordion.Body>
        </Accordion.Item>
    );
}

export default withNamespaces()(ManagementTeam);
